import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  doc, 
  setDoc, 
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaUsers, 
  FaUserShield, 
  FaGraduationCap, 
  FaSearch, 
  FaUserPlus, 
  FaTrash,
  FaLock,
  FaChartLine,
  FaCrown,
  FaUserCog,
  FaFileExcel,
  FaFilePdf,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardList
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import styles from './UserManagementDashboard.module.css';
import Badge from '../components/achievement/Badge';
import { earnedChapterBadges, earnedSubjectCertificates, resolveSubjectLabel } from '../utils/achievements';
import { SUBJECT_CHAPTERS, computeSubjectStats } from '../config/tutorialCatalogue';

const UserManagementDashboard = () => {
  // Auth & Navigation
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();

  // State
  const [hasAccess, setHasAccess] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [users, setUsers] = useState([]);
  const [privilegedUsers, setPrivilegedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, admin, student
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Export Date Filter State
  const [exportDateFilter, setExportDateFilter] = useState('all'); // 30days, 6months, 1year, 2years, all
  
  // Access Management Modal
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [granting, setGranting] = useState(false);
  
  // Role Management Modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);

  // Test Results Modal
  const [testResultsUser, setTestResultsUser] = useState(null);
  const [testResultsData, setTestResultsData] = useState([]);
  const [testResultsLoading, setTestResultsLoading] = useState(false);
  const [testResultsDateFilter, setTestResultsDateFilter] = useState('all');
  const [testResultsPage, setTestResultsPage] = useState(1);
  const [testResultsPerPage, setTestResultsPerPage] = useState(10);

  // Learning progress modal
  const [learningUser, setLearningUser] = useState(null);
  const [learningProgress, setLearningProgress] = useState(null);
  const [learningUserData, setLearningUserData] = useState(null);
  const [learningLoading, setLearningLoading] = useState(false);

  // Analytics
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalAdmins: 0,
    totalStudents: 0,
    totalSuperAdmins: 0,
    privilegedCount: 0
  });

  // Check if current user has dashboard access or is superadmin
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const checkAccess = async () => {
      try {
        // Check if user is superadmin first
        if (userData?.isSuperAdmin === true) {
          setIsSuperAdmin(true);
          setHasAccess(true);
          setCheckingAccess(false);
          
          // Auto-grant dashboard access for superadmin
          const accessRef = doc(db, 'dashboardAccess', currentUser.uid);
          await setDoc(accessRef, {
            hasAccess: true,
            grantedAt: serverTimestamp(),
            grantedBy: 'system',
            grantedByEmail: 'auto-granted (superadmin)',
            isSuperAdmin: true
          }, { merge: true });
          
          return;
        }

        // Otherwise check dashboard access collection
        const accessRef = doc(db, 'dashboardAccess', currentUser.uid);
        const unsubscribe = onSnapshot(accessRef, (docSnap) => {
          if (docSnap.exists() && docSnap.data().hasAccess === true) {
            setHasAccess(true);
            setIsSuperAdmin(docSnap.data().isSuperAdmin === true);
          } else {
            setHasAccess(false);
            setIsSuperAdmin(false);
          }
          setCheckingAccess(false);
        });
        return unsubscribe;
      } catch (error) {
        console.error('Error checking access:', error);
        setCheckingAccess(false);
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [currentUser, navigate, userData]);

  // Fetch all users and privileged users
  useEffect(() => {
    if (!hasAccess) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all users - sorted by newest first
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);

        // Fetch privileged users (real-time listener)
        const privilegedRef = collection(db, 'dashboardAccess');
        const unsubscribePrivileged = onSnapshot(privilegedRef, (snapshot) => {
          const privilegedData = snapshot.docs
            .filter(doc => doc.data().hasAccess === true)
            .map(doc => ({
              userId: doc.id,
              ...doc.data()
            }));
          setPrivilegedUsers(privilegedData);

          // Calculate analytics
          const admins = usersData.filter(u => u.isAdmin === true && u.isSuperAdmin !== true).length;
          const superAdmins = usersData.filter(u => u.isSuperAdmin === true).length;
          const students = usersData.filter(u => u.isAdmin !== true && u.isSuperAdmin !== true).length;
          setAnalytics({
            totalUsers: usersData.length,
            totalAdmins: admins,
            totalStudents: students,
            totalSuperAdmins: superAdmins,
            privilegedCount: privilegedData.length
          });
        });

        setLoading(false);
        return unsubscribePrivileged;
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchData();
  }, [hasAccess]);

  // Grant dashboard access to a user
  const grantAccess = async () => {
    if (!selectedEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    setGranting(true);
    try {
      // Find user by email
      const userToGrant = users.find(u => u.email?.toLowerCase() === selectedEmail.toLowerCase());
      
      if (!userToGrant) {
        toast.error('User not found with this email');
        setGranting(false);
        return;
      }

      // Check if already has access
      const alreadyHasAccess = privilegedUsers.some(p => p.userId === userToGrant.id);
      if (alreadyHasAccess) {
        toast.info('This user already has dashboard access');
        setGranting(false);
        return;
      }

      // Grant access
      const accessRef = doc(db, 'dashboardAccess', userToGrant.id);
      await setDoc(accessRef, {
        hasAccess: true,
        grantedAt: serverTimestamp(),
        grantedBy: currentUser.uid,
        grantedByEmail: currentUser.email
      });

      toast.success(`Access granted to ${userToGrant.username || userToGrant.email}`);
      setSelectedEmail('');
      setShowAccessModal(false);
    } catch (error) {
      console.error('Error granting access:', error);
      toast.error('Failed to grant access');
    } finally {
      setGranting(false);
    }
  };

  // Revoke dashboard access
  const revokeAccess = async (userId, userName) => {
    if (!window.confirm(`Revoke dashboard access for ${userName}?`)) return;

    // Prevent self-revocation
    if (userId === currentUser.uid) {
      toast.error('You cannot revoke your own access');
      return;
    }

    // Check if user is superadmin - cannot revoke superadmin access
    const userToRevoke = users.find(u => u.id === userId);
    if (userToRevoke?.isSuperAdmin === true) {
      toast.error('Cannot revoke access from a SuperAdmin');
      return;
    }

    try {
      const accessRef = doc(db, 'dashboardAccess', userId);
      await deleteDoc(accessRef);
      toast.success(`Access revoked for ${userName}`);
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  // Promote/Demote user role (SuperAdmin only)
  const openTestResults = async (user) => {
    setTestResultsUser(user);
    setTestResultsData([]);
    setTestResultsDateFilter('all');
    setTestResultsPage(1);
    setTestResultsLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', user.id));
      if (snap.exists()) {
        const data = snap.data();
        const tests = (data.testsTaken || []).slice().sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setTestResultsData(tests);
      }
    } catch (e) {
      console.error('Failed to load test results', e);
      toast.error('Failed to load test results');
    } finally {
      setTestResultsLoading(false);
    }
  };

  // Load a student's learning progress (all subjects) from Firestore
  const openLearning = async (user) => {
    setLearningUser(user);
    setLearningProgress(null);
    setLearningUserData(null);
    setLearningLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', user.id));
      const udata = snap.exists() ? snap.data() : {};
      setLearningUserData(udata);
      const progressSnap = await getDocs(collection(db, 'users', user.id, 'progress'));
      const progress = {};
      progressSnap.forEach(d => { progress[d.id] = d.data(); });
      setLearningProgress(progress);
    } catch (e) {
      console.error('Failed to load learning progress', e);
      toast.error('Failed to load learning progress');
    } finally {
      setLearningLoading(false);
    }
  };

  // Promote/Demote user role (SuperAdmin only)
  const handleRoleChange = async (newRole) => {
    if (!selectedUserForRole) return;
    
    setUpdatingRole(true);
    try {
      const userRef = doc(db, 'users', selectedUserForRole.id);
      
      if (newRole === 'admin') {
        await setDoc(userRef, {
          isAdmin: true,
          isSuperAdmin: false
        }, { merge: true });
        toast.success(`${selectedUserForRole.username || selectedUserForRole.email} promoted to Admin`);
      } else if (newRole === 'student') {
        await setDoc(userRef, {
          isAdmin: false,
          isSuperAdmin: false
        }, { merge: true });
        toast.success(`${selectedUserForRole.username || selectedUserForRole.email} demoted to Student`);
      }
      
      setShowRoleModal(false);
      setSelectedUserForRole(null);
      
      // Refresh users list - sorted by newest first
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
      
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update user role');
    } finally {
      setUpdatingRole(false);
    }
  };

  // Filter users based on search and type
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobile?.includes(searchTerm);

    const matchesType = 
      filterType === 'all' ||
      (filterType === 'superadmin' && user.isSuperAdmin === true) ||
      (filterType === 'admin' && user.isAdmin === true && user.isSuperAdmin !== true) ||
      (filterType === 'student' && user.isAdmin !== true && user.isSuperAdmin !== true);

    return matchesSearch && matchesType;
  }).sort((a, b) => {
    // Sort by createdAt descending (newest first)
    const dateA = a.createdAt ? (a.createdAt.seconds ? a.createdAt.seconds : new Date(a.createdAt).getTime() / 1000) : 0;
    const dateB = b.createdAt ? (b.createdAt.seconds ? b.createdAt.seconds : new Date(b.createdAt).getTime() / 1000) : 0;
    return dateB - dateA; // Descending order (newest first)
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Helper function to filter users by date range
  const filterUsersByDateRange = (users) => {
    if (exportDateFilter === 'all') return users;

    const now = new Date();
    const cutoffDate = new Date();

    switch (exportDateFilter) {
      case '30days':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '6months':
        cutoffDate.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      case '2years':
        cutoffDate.setFullYear(now.getFullYear() - 2);
        break;
      default:
        return users;
    }

    return users.filter(user => {
      if (!user.createdAt) return false;
      const userDate = new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt);
      return userDate >= cutoffDate;
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const dateFilteredUsers = filterUsersByDateRange(filteredUsers);
      
      if (dateFilteredUsers.length === 0) {
        toast.warning('No users found in the selected date range');
        return;
      }

      const exportData = dateFilteredUsers.map(user => ({
        'Name': user.username || 'N/A',
        'Email': user.email || 'N/A',
        'Phone': user.mobile || 'N/A',
        'Class': user.class || 'N/A',
        'Preparing For': user.preparingFor || 'N/A',
        'Role': user.isSuperAdmin ? 'SuperAdmin' : user.isAdmin ? 'Admin' : 'Student',
        'Created At': user.createdAt ? new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt).toLocaleDateString('en-IN') : 'N/A',
        'Dashboard Access': privilegedUsers.some(p => p.userId === user.id) ? 'Yes' : 'No'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      
      // Generate filename with timestamp and filter info
      const timestamp = new Date().toISOString().split('T')[0];
      const filterLabel = exportDateFilter === 'all' ? 'All' : exportDateFilter === '30days' ? 'Last30Days' : exportDateFilter === '6months' ? 'Last6Months' : exportDateFilter === '1year' ? 'Last1Year' : 'Last2Years';
      XLSX.writeFile(wb, `PrepXPathshala_Users_${filterLabel}_${timestamp}.xlsx`);
      
      toast.success(`Excel file exported successfully! (${dateFilteredUsers.length} users)`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export Excel file');
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const dateFilteredUsers = filterUsersByDateRange(filteredUsers);
      
      if (dateFilteredUsers.length === 0) {
        toast.warning('No users found in the selected date range');
        return;
      }

      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.text('PrepX Pathshala - User Management', 14, 22);
      
      // Add metadata
      doc.setFontSize(11);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 32);
      const filterLabels = {
        'all': 'All Time',
        '30days': 'Last 30 Days',
        '6months': 'Last 6 Months',
        '1year': 'Last 1 Year',
        '2years': 'Last 2 Years'
      };
      doc.text(`Date Filter: ${filterLabels[exportDateFilter]}`, 14, 38);
      doc.text(`Total Users: ${dateFilteredUsers.length}`, 14, 44);
      
      // Prepare table data
      const tableData = dateFilteredUsers.map(user => [
        user.username || 'N/A',
        user.email || 'N/A',
        user.mobile || 'N/A',
        user.class || 'N/A',
        user.preparingFor || 'N/A',
        user.isSuperAdmin ? 'SuperAdmin' : user.isAdmin ? 'Admin' : 'Student',
        user.createdAt ? new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt).toLocaleDateString('en-IN') : 'N/A',
        privilegedUsers.some(p => p.userId === user.id) ? 'Yes' : 'No'
      ]);

      // Add table
      doc.autoTable({
        startY: 50,
        head: [['Name', 'Email', 'Phone', 'Class', 'Preparing For', 'Role', 'Created At', 'Dashboard']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [103, 58, 183] },
        styles: { fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 35 },
          2: { cellWidth: 22 },
          3: { cellWidth: 12 },
          4: { cellWidth: 22 },
          5: { cellWidth: 18 },
          6: { cellWidth: 20 },
          7: { cellWidth: 16 }
        }
      });
      
      // Generate filename with timestamp and filter info
      const timestamp = new Date().toISOString().split('T')[0];
      const filterLabel = exportDateFilter === 'all' ? 'All' : exportDateFilter === '30days' ? 'Last30Days' : exportDateFilter === '6months' ? 'Last6Months' : exportDateFilter === '1year' ? 'Last1Year' : 'Last2Years';
      doc.save(`PrepXPathshala_Users_${filterLabel}_${timestamp}.pdf`);
      
      toast.success(`PDF file exported successfully! (${dateFilteredUsers.length} users)`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export PDF file');
    }
  };

  // Loading and Access Check States
  if (checkingAccess) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Checking access permissions...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className={styles.accessDenied}>
        <FaLock className={styles.lockIcon} />
        <h1>Access Denied</h1>
        <p>You don't have permission to view this dashboard.</p>
        <p className={styles.contactInfo}>
          Contact an administrator to request access.
        </p>
        <button onClick={() => navigate('/')} className={styles.backButton}>
          Go to Home
        </button>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <h1 className={styles.title}>
              <FaChartLine /> User Management Dashboard
            </h1>
            <p className={styles.subtitle}>
              Comprehensive view of all platform users and analytics
            </p>
          </div>
          {isSuperAdmin && (
            <button 
              className={styles.grantAccessBtn}
              onClick={() => setShowAccessModal(true)}
            >
              <FaUserPlus /> Grant Dashboard Access
            </button>
          )}
        </div>
      </header>

      {/* Analytics Cards */}
      <section className={styles.analyticsSection}>
        <div className={styles.analyticsGrid}>
          <div className={`${styles.analyticsCard} ${styles.cardBlue}`}>
            <div className={styles.cardIcon}>
              <FaUsers />
            </div>
            <div className={styles.cardContent}>
              <h3>Total Users</h3>
              <p className={styles.cardNumber}>{analytics.totalUsers}</p>
            </div>
          </div>

          <div className={`${styles.analyticsCard} ${styles.cardGreen}`}>
            <div className={styles.cardIcon}>
              <FaUserShield />
            </div>
            <div className={styles.cardContent}>
              <h3>Admins</h3>
              <p className={styles.cardNumber}>{analytics.totalAdmins}</p>
            </div>
          </div>

          <div className={`${styles.analyticsCard} ${styles.cardGold}`}>
            <div className={styles.cardIcon}>
              <FaCrown />
            </div>
            <div className={styles.cardContent}>
              <h3>SuperAdmins</h3>
              <p className={styles.cardNumber}>{analytics.totalSuperAdmins}</p>
            </div>
          </div>

          <div className={`${styles.analyticsCard} ${styles.cardPurple}`}>
            <div className={styles.cardIcon}>
              <FaGraduationCap />
            </div>
            <div className={styles.cardContent}>
              <h3>Students</h3>
              <p className={styles.cardNumber}>{analytics.totalStudents}</p>
            </div>
          </div>

          <div className={`${styles.analyticsCard} ${styles.cardOrange}`}>
            <div className={styles.cardIcon}>
              <FaLock />
            </div>
            <div className={styles.cardContent}>
              <h3>Privileged Users</h3>
              <p className={styles.cardNumber}>{analytics.privilegedCount}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privileged Users Section */}
      <section className={styles.privilegedSection}>
        <h2 className={styles.sectionTitle}>
          <FaUserShield /> Users with Dashboard Access
        </h2>
        <div className={styles.privilegedList}>
          {privilegedUsers.length === 0 ? (
            <p className={styles.emptyState}>No privileged users yet</p>
          ) : (
            privilegedUsers.map(priv => {
              const user = users.find(u => u.id === priv.userId);
              return (
                <div key={priv.userId} className={styles.privilegedCard}>
                  <img 
                    src={user?.photoURL || 'https://via.placeholder.com/40'} 
                    alt={user?.username || 'User'}
                    className={styles.userAvatar}
                  />
                  <div className={styles.privilegedInfo}>
                    <h4>{user?.username || 'Unknown User'}</h4>
                    <p>{user?.email}</p>
                  </div>
                  {isSuperAdmin && priv.userId !== currentUser.uid && (
                    <button
                      className={styles.revokeBtn}
                      onClick={() => revokeAccess(priv.userId, user?.username || user?.email)}
                    >
                      <FaTrash /> Revoke
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Users Table */}
      <section className={styles.usersSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>
              <FaUsers /> All Platform Users
            </h2>
            <div className={styles.exportControls}>
              <div className={styles.exportDateFilter}>
                <label htmlFor="exportDateRange">Export Range:</label>
                <select 
                  id="exportDateRange"
                  value={exportDateFilter} 
                  onChange={(e) => setExportDateFilter(e.target.value)}
                  className={styles.dateFilterSelect}
                >
                  <option value="30days">Last 30 Days</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="1year">Last 1 Year</option>
                  <option value="2years">Last 2 Years</option>
                  <option value="all">All Time</option>
                </select>
              </div>
              <div className={styles.exportButtons}>
                <button onClick={exportToExcel} className={styles.exportBtn}>
                  <FaFileExcel /> Export Excel
                </button>
                <button onClick={exportToPDF} className={styles.exportBtnPdf}>
                  <FaFilePdf /> Export PDF
                </button>
              </div>
            </div>
          </div>
          
          {/* Filters */}
          <div className={styles.filters}>
            <div className={styles.searchBox}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.filterButtons}>
              <button 
                className={filterType === 'all' ? styles.filterActive : styles.filterBtn}
                onClick={() => setFilterType('all')}
              >
                All ({users.length})
              </button>
              <button 
                className={filterType === 'admin' ? styles.filterActive : styles.filterBtn}
                onClick={() => setFilterType('admin')}
              >
                Admins ({analytics.totalAdmins})
              </button>
              <button 
                className={filterType === 'student' ? styles.filterActive : styles.filterBtn}
                onClick={() => setFilterType('student')}
              >
                Students ({analytics.totalStudents})
              </button>
              {analytics.totalSuperAdmins > 0 && (
                <button 
                  className={filterType === 'superadmin' ? styles.filterActive : styles.filterBtn}
                  onClick={() => setFilterType('superadmin')}
                >
                  SuperAdmins ({analytics.totalSuperAdmins})
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className={styles.tableLoading}>
            <div className={styles.spinner}></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.usersTable}>
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Class</th>
                  <th>Preparing For</th>
                  <th>Role</th>
                  <th>Created At</th>
                  <th>Dashboard Access</th>
                  {isSuperAdmin && <th>Actions</th>}
                  <th>Tests</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                  <td colSpan={isSuperAdmin ? "11" : "10"} className={styles.noResults}>
                      No users found matching your criteria
                    </td>
                  </tr>
                ) : (
                  currentUsers.map(user => {
                    const hasPrivilegedAccess = privilegedUsers.some(p => p.userId === user.id);
                    return (
                      <tr key={user.id}>
                        <td>
                          <img 
                            src={user.photoURL || 'https://via.placeholder.com/40'} 
                            alt={user.username || 'User'}
                            className={styles.tableAvatar}
                          />
                        </td>
                        <td className={styles.userName}>{user.username || 'N/A'}</td>
                        <td>{user.email || 'N/A'}</td>
                        <td>{user.mobile || 'N/A'}</td>
                        <td>{user.class || 'N/A'}</td>
                        <td>{user.preparingFor || 'N/A'}</td>
                        <td>
                          {user.isSuperAdmin ? (
                            <span className={styles.badgeSuperAdmin}>
                              <FaCrown /> SuperAdmin
                            </span>
                          ) : user.isAdmin ? (
                            <span className={styles.badgeAdmin}>
                              Admin
                            </span>
                          ) : (
                            <span className={styles.badgeStudent}>
                              Student
                            </span>
                          )}
                        </td>
                        <td className={styles.dateCell}>
                          {user.createdAt ? (
                            new Date(user.createdAt.seconds ? user.createdAt.seconds * 1000 : user.createdAt).toLocaleDateString('en-IN', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })
                          ) : 'N/A'}
                        </td>
                        <td>
                          <span className={hasPrivilegedAccess ? styles.badgeGranted : styles.badgeNoAccess}>
                            {hasPrivilegedAccess ? '✓ Granted' : '✗ No Access'}
                          </span>
                        </td>
                        <td>
                          {isSuperAdmin && user.id !== currentUser.uid && !user.isSuperAdmin && (
                            <button
                              className={styles.manageRoleBtn}
                              onClick={() => {
                                setSelectedUserForRole(user);
                                setShowRoleModal(true);
                              }}
                            >
                              <FaUserCog /> Manage Role
                            </button>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              className={styles.testResultsBtn}
                              onClick={() => openTestResults(user)}
                              title="View test results"
                            >
                              <FaClipboardList /> Tests
                            </button>
                            <button
                              className={styles.testResultsBtn}
                              onClick={() => openLearning(user)}
                              title="View learning progress, badges & certificates"
                            >
                              <FaGraduationCap /> Learning
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && filteredUsers.length > 0 && (
          <div className={styles.paginationContainer}>
            <div className={styles.paginationInfo}>
              <span>
                Showing {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
              </span>
              <div className={styles.itemsPerPageSelector}>
                <label htmlFor="itemsPerPage">Show:</label>
                <select 
                  id="itemsPerPage"
                  value={itemsPerPage} 
                  onChange={handleItemsPerPageChange}
                  className={styles.itemsPerPageSelect}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span>per page</span>
              </div>
            </div>

            <div className={styles.paginationControls}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={styles.paginationBtn}
                title="Previous Page"
              >
                <FaChevronLeft />
              </button>

              {/* Page Numbers */}
              <div className={styles.pageNumbers}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={
                          currentPage === pageNum
                            ? styles.paginationBtnActive
                            : styles.paginationBtn
                        }
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 ||
                    pageNum === currentPage + 2
                  ) {
                    return <span key={pageNum} className={styles.paginationEllipsis}>...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={styles.paginationBtn}
                title="Next Page"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Grant Access Modal */}
      {showAccessModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAccessModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><FaUserPlus /> Grant Dashboard Access</h3>
              <button 
                className={styles.modalClose}
                onClick={() => setShowAccessModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Enter the email address of the user you want to grant dashboard access to.
              </p>
              <input
                type="email"
                placeholder="user@example.com"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                className={styles.modalInput}
                onKeyPress={(e) => e.key === 'Enter' && grantAccess()}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelBtn}
                onClick={() => setShowAccessModal(false)}
              >
                Cancel
              </button>
              <button 
                className={styles.modalGrantBtn}
                onClick={grantAccess}
                disabled={granting}
              >
                {granting ? 'Granting...' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal (SuperAdmin Only) */}
      {showRoleModal && selectedUserForRole && (
        <div className={styles.modalOverlay} onClick={() => setShowRoleModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><FaUserCog /> Manage User Role</h3>
              <button 
                className={styles.modalClose}
                onClick={() => setShowRoleModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.userInfoCard}>
                <img 
                  src={selectedUserForRole.photoURL || 'https://via.placeholder.com/60'} 
                  alt={selectedUserForRole.username}
                  className={styles.modalUserAvatar}
                />
                <div>
                  <h4>{selectedUserForRole.username || 'Unknown User'}</h4>
                  <p>{selectedUserForRole.email}</p>
                  <p className={styles.currentRoleBadge}>
                    Current Role: {selectedUserForRole.isAdmin ? 'Admin' : 'Student'}
                  </p>
                </div>
              </div>
              <p className={styles.modalDescription}>
                Select the new role for this user:
              </p>
              <div className={styles.roleOptions}>
                {!selectedUserForRole.isAdmin && (
                  <button
                    className={styles.roleOptionBtn}
                    onClick={() => handleRoleChange('admin')}
                    disabled={updatingRole}
                  >
                    <FaUserShield /> Promote to Admin
                  </button>
                )}
                {selectedUserForRole.isAdmin && (
                  <button
                    className={`${styles.roleOptionBtn} ${styles.demoteBtn}`}
                    onClick={() => handleRoleChange('student')}
                    disabled={updatingRole}
                  >
                    <FaGraduationCap /> Demote to Student
                  </button>
                )}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.modalCancelBtn}
                onClick={() => setShowRoleModal(false)}
                disabled={updatingRole}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== LEARNING PROGRESS MODAL ========== */}
      {learningUser && (() => {
        const allProgress = learningProgress || {};
        const badges = learningProgress ? earnedChapterBadges(allProgress) : [];
        const certs = learningProgress ? earnedSubjectCertificates(allProgress) : [];
        const enrolledPrograms = learningUserData?.enrolledPrograms || [];
        const enrollMap = learningUserData?.enrollmentDetails || null;
        const enrollmentDetails = enrollMap
          ? (enrollMap.latest && typeof enrollMap.latest === 'object' ? enrollMap.latest : (enrollMap.fullName ? enrollMap : null))
          : null;
        const allEnrollments = enrollMap
          ? Object.entries(enrollMap).filter(([k, v]) => k !== 'latest' && v && typeof v === 'object' && v.fullName)
          : [];
        const studentPreparingFor = learningUserData?.preparingFor || enrollmentDetails?.preparingFor || '';
        const subjectKeys = Object.keys(SUBJECT_CHAPTERS).filter(k => {
          const stats = computeSubjectStats(k, allProgress);
          return stats.done > 0;
        });
        const uName = learningUser.username || learningUser.email || learningUser.id;

        return (
          <div className={styles.trModalBackdrop} onClick={() => setLearningUser(null)}>
            <div className={styles.trModalBox} onClick={e => e.stopPropagation()}>
              <div className={styles.trModalHeader}>
                <div>
                  <h2 className={styles.trModalTitle}><FaGraduationCap /> Learning Progress</h2>
                  <p className={styles.trModalSub}><strong>{uName}</strong></p>
                </div>
                <button className={styles.trCloseBtn} onClick={() => setLearningUser(null)}>×</button>
              </div>

              {learningLoading ? (
                <div className={styles.trLoading}>
                  <div className={styles.spinner} style={{ width: 28, height: 28 }} />
                  Loading progress...
                </div>
              ) : (
                <div style={{ padding: '0 22px 22px', overflowY: 'auto', maxHeight: '70vh' }}>
                  {/* Enrolled programs */}
                  <h4 style={{ margin: '14px 0 6px', fontSize: '0.85rem', color: '#334155' }}>Enrolled Programs</h4>
                  {enrolledPrograms.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {enrolledPrograms.map(p => (
                        <span key={p} style={{ background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.74rem', padding: '4px 12px', borderRadius: 999, textTransform: 'uppercase' }}>{p}</span>
                      ))}
                    </div>
                  ) : <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>Not enrolled in any program.</p>}

                  {/* Enrollment details — one card per enrolled program */}
                  {(enrollmentDetails || allEnrollments.length > 0) && (
                    <>
                      <h4 style={{ margin: '16px 0 6px', fontSize: '0.85rem', color: '#334155' }}>Enrollment Details</h4>
                      {(allEnrollments.length > 0 ? allEnrollments : [['latest', enrollmentDetails]]).map(([pid, d]) => (
                        <div key={pid} style={{ marginBottom: 10 }}>
                          {d.programLabel && <p style={{ margin: '0 0 4px', fontSize: '0.7rem', fontWeight: 800, color: '#1d4ed8', textTransform: 'uppercase' }}>{d.programLabel}</p>}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: '0.78rem' }}>
                            <div><strong>Class:</strong> {d.className}</div>
                            <div><strong>Preparing:</strong> {d.preparingFor}</div>
                            <div><strong>Phone:</strong> {d.phone}</div>
                            <div><strong>DOB:</strong> {d.dob}</div>
                            <div><strong>Father:</strong> {d.fatherName}</div>
                            <div><strong>Mother:</strong> {d.motherName}</div>
                            <div style={{ gridColumn: '1/-1' }}><strong>Mode:</strong> {d.prepMode}{d.coachingName ? ` · ${d.coachingName}` : ''}</div>
                            {d.additional && <div style={{ gridColumn: '1/-1' }}><strong>Notes:</strong> {d.additional}</div>}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Subject progress */}
                  <h4 style={{ margin: '16px 0 6px', fontSize: '0.85rem', color: '#334155' }}>Subject Progress</h4>
                  {subjectKeys.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {subjectKeys.map(k => {
                        const s = computeSubjectStats(k, allProgress);
                        return (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.78rem' }}>
                            <span style={{ minWidth: 150, fontWeight: 600, color: '#334155' }}>{k}</span>
                            <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${s.pct}%`, background: 'linear-gradient(90deg,#3b82f6,#6366f1)' }} />
                            </div>
                            <span style={{ minWidth: 90, textAlign: 'right', color: '#64748b' }}>{s.done}/{s.total} · {s.pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>No learning activity yet.</p>}

                  {/* Certificates */}
                  <h4 style={{ margin: '16px 0 6px', fontSize: '0.85rem', color: '#334155' }}>Certificates Earned ({certs.length})</h4>
                  {certs.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {certs.map(c => (
                        <span key={c.subjectKey} style={{ border: `1.5px solid ${c.meta.hue}`, color: c.meta.hue, fontWeight: 700, fontSize: '0.74rem', padding: '4px 12px', borderRadius: 999 }}>
                          📜 {resolveSubjectLabel(c.subjectKey, studentPreparingFor)}
                        </span>
                      ))}
                    </div>
                  ) : <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>No subject certificates yet.</p>}

                  {/* Badges */}
                  <h4 style={{ margin: '16px 0 10px', fontSize: '0.85rem', color: '#334155' }}>Chapter Badges ({badges.length})</h4>
                  {badges.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 18px', paddingBottom: 22 }}>
                      {badges.map(b => (
                        <Badge key={`${b.subjectKey}:${b.chapterId}`}
                          emoji={b.meta.emoji} hue={b.meta.hue} hue2={b.meta.hue2}
                          title={b.title} subtitle={b.meta.label}
                          studentName={learningUser.username || ''} />
                      ))}
                    </div>
                  ) : <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>No chapter badges yet.</p>}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ========== TEST RESULTS MODAL ========== */}
      {testResultsUser && (() => {
        const getDateLimit = () => {
          if (testResultsDateFilter === 'all') return null;
          const d = new Date();
          if (testResultsDateFilter === '30d') d.setDate(d.getDate() - 30);
          else if (testResultsDateFilter === '3m') d.setMonth(d.getMonth() - 3);
          else if (testResultsDateFilter === '6m') d.setMonth(d.getMonth() - 6);
          else if (testResultsDateFilter === '1y') d.setFullYear(d.getFullYear() - 1);
          else if (testResultsDateFilter === '2y') d.setFullYear(d.getFullYear() - 2);
          return d;
        };
        const dateLimit = getDateLimit();
        const filtered = testResultsData.filter(t => {
          if (!dateLimit) return true;
          const d = t.date ? new Date(t.date) : null;
          return d && !isNaN(d) && d >= dateLimit;
        });
        const totalPages = Math.max(1, Math.ceil(filtered.length / testResultsPerPage));
        const safePage = Math.min(testResultsPage, totalPages);
        const paginated = filtered.slice((safePage - 1) * testResultsPerPage, safePage * testResultsPerPage);
        const userName = testResultsUser.username || testResultsUser.email || testResultsUser.id;

        return (
          <div className={styles.trModalBackdrop} onClick={() => setTestResultsUser(null)}>
            <div className={styles.trModalBox} onClick={e => e.stopPropagation()}>

              <div className={styles.trModalHeader}>
                <div>
                  <h2 className={styles.trModalTitle}><FaClipboardList /> Test Results</h2>
                  <p className={styles.trModalSub}>
                    <strong>{userName}</strong> &mdash; {testResultsData.length} total attempt{testResultsData.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button className={styles.trCloseBtn} onClick={() => setTestResultsUser(null)}>×</button>
              </div>

              <div className={styles.trFilters}>
                <label className={styles.trFilterLabel}>Filter by date:</label>
                <select
                  value={testResultsDateFilter}
                  onChange={e => { setTestResultsDateFilter(e.target.value); setTestResultsPage(1); }}
                  className={styles.trSelect}
                >
                  <option value="all">All Time</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="3m">Last 3 Months</option>
                  <option value="6m">Last 6 Months</option>
                  <option value="1y">Last Year</option>
                  <option value="2y">Last 2 Years</option>
                </select>
                <span className={styles.trCount}>
                  {filtered.length}{testResultsDateFilter !== 'all' ? ` of ${testResultsData.length}` : ''} result{filtered.length !== 1 ? 's' : ''}
                </span>
              </div>

              {testResultsLoading ? (
                <div className={styles.trLoading}>
                  <div className={styles.spinner} style={{width:28,height:28}} />
                  Loading results...
                </div>
              ) : filtered.length === 0 ? (
                <p className={styles.trEmpty}>
                  {testResultsData.length === 0
                    ? 'This student has not attempted any test yet.'
                    : 'No results found for the selected time period.'}
                </p>
              ) : (
                <>
                  <div className={styles.trTableWrap}>
                    <table className={styles.trTable}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Test Name</th>
                          <th>Score / Max</th>
                          <th>Attempted</th>
                          <th>Correct</th>
                          <th>Incorrect</th>
                          <th>Attempt No.</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((t, idx) => {
                          const pct = t.total > 0 ? Math.round((t.score / t.total) * 100) : null;
                          return (
                            <tr key={idx}>
                              <td>{(safePage - 1) * testResultsPerPage + idx + 1}</td>
                              <td className={styles.trTestName}>{t.testName || 'N/A'}</td>
                              <td>
                                <span className={t.score >= 0 ? styles.trGreen : styles.trRed}>
                                  {t.score ?? 'N/A'} / {t.total ?? 'N/A'}
                                </span>
                                {pct !== null && <span className={styles.trPct}> ({pct}%)</span>}
                              </td>
                              <td>{t.attempted ?? 'N/A'} / {t.totalQuestions ?? 'N/A'}</td>
                              <td><span className={styles.trGreen}>{t.correct ?? 'N/A'}</span></td>
                              <td><span className={styles.trRed}>{t.incorrect ?? 'N/A'}</span></td>
                              <td>{t.attempt ?? '—'}</td>
                              <td>{t.date ? new Date(t.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.trPagination}>
                    <span className={styles.trPagInfo}>
                      Showing {(safePage - 1) * testResultsPerPage + 1}–{Math.min(safePage * testResultsPerPage, filtered.length)} of {filtered.length}
                    </span>
                    <div className={styles.trPagControls}>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(1)} disabled={safePage === 1}>«</button>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(p => p - 1)} disabled={safePage === 1}>‹ Prev</button>
                      <span className={styles.trPageInfo}>Page {safePage} of {totalPages}</span>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(p => p + 1)} disabled={safePage >= totalPages}>Next ›</button>
                      <button className={styles.trPageBtn} onClick={() => setTestResultsPage(totalPages)} disabled={safePage >= totalPages}>»</button>
                      <select
                        value={testResultsPerPage}
                        onChange={e => { setTestResultsPerPage(Number(e.target.value)); setTestResultsPage(1); }}
                        className={styles.trPerPage}
                      >
                        <option value={10}>10 / page</option>
                        <option value={20}>20 / page</option>
                        <option value={50}>50 / page</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default UserManagementDashboard;
